import ModalViewModel from 'infra/ModalViewModel'
import { action, observable, makeObservable } from 'mobx'
import DocumentType from 'documents/DocumentType'
import { DocumentCreationItemViewModel } from './DocumentCreationItem/DocumentCreationItemViewModel'
import Job from 'jobs/Job'
import UploadBarViewModel from '../DocumentRoot/UploadBar/UploadBarViewModel'
import type { UploadDocumentRequest } from 'documents/DocumentStore'
import { Id } from 'types/definitions'

export class DocumentCreationModalViewModel extends ModalViewModel {
  @observable documentCreationItemVMs: DocumentCreationItemViewModel[] = []
  @observable dragging = false
  @observable typesSelected: number = 0
  @observable filesNotMatchingClientNames: File[] = []

  readonly uploadBarVM: UploadBarViewModel
  readonly job: Job
  readonly documentTypes: DocumentType[]

  /**
   * Get client names for validation purposes
   */
  get clientNames() {
    return this.job.collaborators
      .filter((c) => c.type === 'CLIENT')
      .map((c) => ({
        firstName: c.member.givenName,
        lastName: c.member.familyName,
      }))
  }

  constructor(
    files: File[],
    documentTypes: DocumentType[],
    job: Job,
    uploadBarVM: UploadBarViewModel,
  ) {
    super()
    makeObservable(this)
    for (const file of files) {
      this.documentCreationItemVMs.push(
        new DocumentCreationItemViewModel(
          file,
          documentTypes,
          this.incrementTypesSelected,
        ),
      )
    }
    this.documentTypes = documentTypes
    this.uploadBarVM = uploadBarVM
    this.job = job
  }

  @action.bound
  setDragging() {
    this.dragging = true
  }

  @action.bound
  stopDragging() {
    this.dragging = false
  }

  @action.bound
  removeFile(index: number) {
    if (this.documentCreationItemVMs[index].typeName !== 'Select type') {
      this.typesSelected--
    }
    this.documentCreationItemVMs.splice(index, 1)
  }

  @action.bound
  clearFiles() {
    this.documentCreationItemVMs = []
  }

  @action.bound
  addFiles(files: FileList) {
    Array.from(files).forEach((file) =>
      this.documentCreationItemVMs.push(
        new DocumentCreationItemViewModel(
          file,
          this.documentTypes,
          this.incrementTypesSelected,
        ),
      ),
    )
  }

  @action.bound
  incrementTypesSelected() {
    this.typesSelected++
  }

  @action.bound
  setFilesNotMatchingClientNames(files: File[]) {
    this.filesNotMatchingClientNames = files
  }

  async uploadFiles() {
    const files: UploadDocumentRequest[] = this.documentCreationItemVMs.map(
      (vm) => ({
        file: vm.file,
        workspaceId: Id.assert(this.job.workspace.id),
        jobId: Id.assert(this.job.id),
        documentTypeId: vm.selectedType
          ? Id.assert(vm.selectedType.id)
          : undefined,
      }),
    )

    try {
      await this.uploadBarVM.uploadFiles(files)
      return true
    } catch (error) {
      console.error('Upload failed:', error)
      return false
    }
  }

  close() {
    super.close()
  }
}
